import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api, { tokenStore } from '@/lib/api';
import { MODULES } from '@/lib/modules';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const login = async ({ email, password, org_id }) => {
    const { data } = await api.post('/auth/login', { email, password, org_id });
    tokenStore.set(data.access_token, data.refresh_token);
    // Strip tokens before storing user
    const { access_token, refresh_token, ...userData } = data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    tokenStore.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function ProtectedRoute({ children, requireSuperAdmin = false, allowForcedChange = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="flex items-center gap-3 text-neutral-500">
          <div className="h-2 w-2 rounded-full bg-amber animate-pulse" />
          <div className="h-2 w-2 rounded-full bg-amber animate-pulse" style={{ animationDelay: '0.15s' }} />
          <div className="h-2 w-2 rounded-full bg-amber animate-pulse" style={{ animationDelay: '0.3s' }} />
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.must_change_password && !allowForcedChange) {
    return <Navigate to="/change-password" replace />;
  }
  if (requireSuperAdmin && user.role !== 'super_admin') {
    // Team-members → self-service. Security officers → HR shell. Everyone else → first valid module.
    if (user.role === 'team_member') return <Navigate to="/my-workspace" replace />;
    if (user.role === 'security_officer') return <Navigate to="/dashboard/hr" replace />;
    const validMods = (user.modules || []).filter((s) => MODULES.find((m) => m.slug === s));
    return <Navigate to={validMods[0] ? `/dashboard/${validMods[0]}` : '/team'} replace />;
  }
  return children;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' · ');
  }
  return String(detail);
}

/**
 * True only when the user has more than one assigned role.
 * Backing shape: `user.roles: string[]` (preferred), or falls back to
 * a single-element array from `user.role`.  Role-switch UI MUST call this
 * helper — never render a switcher for single-role users.
 */
export function hasMultipleRoles(user) {
  if (!user) return false;
  const roles = Array.isArray(user.roles) && user.roles.length > 0
    ? user.roles
    : (user.role ? [user.role] : []);
  return roles.length > 1;
}
